import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { MapPin, AlertTriangle, ArrowRight, ChevronRight, Shield, Building } from "lucide-react";
import { API, scrollToTop, setCanonical } from "../lib/config";
import { useSettings, getAffiliate } from "../lib/useSettings";
import { Nav, Footer, HeroBanner, CookieConsent, QuizTeaser } from "../components/layout";
import { ChatBot, ExitIntentPopup } from "../components/chat";
import { MiniQuiz } from "../components/quiz";
import { AdSenseOnly } from "../components/ads";

export default function CityPage() {
  const { slug } = useParams();
  const [city, setCity] = useState(null);
  const [loading, setLoading] = useState(true);
  const settings = useSettings();

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/city/${slug}`).then(r => {
      const c = r.data;
      setCity(c);
      setCanonical(`/quotes/${slug}`);
      document.title = `Auto Insurance Guide for ${c.city}, ${c.state} | QuotesByCity`;
      const meta = document.querySelector('meta[name="description"]') || document.createElement("meta");
      meta.name = "description";
      meta.content = `Auto insurance guide for ${c.city}, ${c.state}. ${c.state_name || c.state} minimum requirements, average rates, local risk factors, and coverage tips.`;
      if (!meta.parentNode) document.head.appendChild(meta);
      const schemas = [
        ["city-faq", { "@context": "https://schema.org", "@type": "FAQPage", "mainEntity": [
          { "@type": "Question", "name": `What is the minimum auto insurance in ${c.state_name || c.state}?`, "acceptedAnswer": { "@type": "Answer", "text": `${c.state_name || c.state} requires a minimum of ${c.state_insurance?.min_auto || 'varies'} liability coverage. Most experts recommend coverage well above state minimums to protect your assets.` }},
          { "@type": "Question", "name": `What is the average auto insurance cost in ${c.city}?`, "acceptedAnswer": { "@type": "Answer", "text": `The average annual auto premium in ${c.state_name || c.state} is approximately $${c.state_insurance?.avg_auto_premium?.toLocaleString() || 'varies'}. Rates vary based on driving record, vehicle, and coverage level.` }},
          { "@type": "Question", "name": `What coverage do I need in ${c.city}, ${c.state}?`, "acceptedAnswer": { "@type": "Answer", "text": `${c.city} drivers need at minimum ${c.state_insurance?.min_auto || 'state-required'} liability coverage. Given local risk factors, most ${c.city} drivers benefit from adding collision, comprehensive, and uninsured motorist coverage.` }},
          { "@type": "Question", "name": `How can I lower my auto insurance rates in ${c.city}?`, "acceptedAnswer": { "@type": "Answer", "text": `${c.city} drivers can lower rates by comparing quotes annually, maintaining a clean driving record, raising deductibles, bundling policies, and asking about available discounts.` }},
        ]}],
        ["city-bread", { "@context": "https://schema.org", "@type": "BreadcrumbList", "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.quotesbycity.com" },
          { "@type": "ListItem", "position": 2, "name": c.state_name || c.state, "item": `https://www.quotesbycity.com/states/${(c.state_name || c.state).toLowerCase().replace(' ', '-')}` },
          { "@type": "ListItem", "position": 3, "name": `${c.city}, ${c.state}`, "item": `https://www.quotesbycity.com/quotes/${slug}` },
        ]}],
        ["city-service", { "@context": "https://schema.org", "@type": "Service", "name": `Auto Insurance Guide for ${c.city}, ${c.state}`, "description": `Free auto insurance education for ${c.city} drivers. Learn about coverage requirements, average rates, and local risk factors.`, "provider": { "@type": "Organization", "name": "QuotesByCity", "url": "https://www.quotesbycity.com" }, "areaServed": { "@type": "City", "name": c.city, "containedInPlace": { "@type": "State", "name": c.state_name || c.state }}, "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" }}],
        ["city-speak", { "@context": "https://schema.org", "@type": "WebPage", "name": `Auto Insurance in ${c.city}, ${c.state}`, "description": `Auto insurance in ${c.city}, ${c.state} requires a minimum of ${c.state_insurance?.min_auto || 'varies'} liability coverage. Free education guide for ${c.city} drivers.`, "speakable": { "@type": "SpeakableSpecification", "cssSelector": ["h1", ".speakable-intro", "[data-testid='state-requirements']"] }, "url": `https://www.quotesbycity.com/quotes/${slug}` }],
      ];
      schemas.forEach(([id, schema]) => {
        let el = document.getElementById(`schema-${id}`);
        if (!el) { el = document.createElement("script"); el.id = `schema-${id}`; el.type = "application/ld+json"; document.head.appendChild(el); }
        el.textContent = JSON.stringify(schema);
      });
    }).catch(() => setCity(null)).finally(() => setLoading(false));
    return () => { ["city-faq", "city-bread", "city-service", "city-speak"].forEach(id => { const el = document.getElementById(`schema-${id}`); if (el) el.remove(); }); };
  }, [slug]);

  if (loading) return <div className="min-h-screen bg-[#FAFAFA]"><Nav /><div className="max-w-4xl mx-auto px-4 py-20 text-center text-slate-400">Loading...</div></div>;
  if (!city) return <div className="min-h-screen bg-[#FAFAFA]"><Nav /><div className="max-w-4xl mx-auto px-4 py-20 text-center"><h1 className="text-2xl font-bold text-slate-900">City Not Found</h1><Link to="/find-your-city" className="text-sky-700 mt-4 inline-block">Search for your city</Link></div><Footer /></div>;

  const stIns = city.state_insurance || {};
  return (
    <div className="bg-[#FAFAFA] min-h-screen">
      <Nav />
      <HeroBanner title={`Auto Insurance in ${city.city}, ${city.state}`} subtitle={`Coverage guide, state requirements, and local risk factors for ${city.city} drivers`} badge={`${city.state_name || city.state} Auto Insurance`} />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">

        {/* GEO: Definition-lead sentence for AI engine citation */}
        <p className="speakable-intro text-sm text-sky-700 font-medium mb-6">
          Auto insurance in {city.city}, {city.state} is required by law with a minimum of {stIns.min_auto || "state-required"} liability coverage — QuotesByCity provides a free education guide to help {city.city} drivers understand their options and find the best rates.
        </p>

        <div className="flex flex-col lg:flex-row gap-8">
          <div className="flex-1 min-w-0 space-y-8">

            {/* STATE REQUIREMENTS */}
            <section className="bg-white rounded-xl border border-slate-200 p-6" data-testid="state-requirements">
              <h2 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>{city.state_name || city.state} Minimum Auto Insurance Requirements</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div className="bg-sky-50 rounded-lg p-4 border border-sky-200">
                  <p className="text-xs text-sky-600 font-semibold uppercase tracking-wider mb-1">Minimum Liability</p>
                  <p className="text-lg font-bold text-slate-900">{stIns.min_auto || "Varies"}</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                  <p className="text-xs text-amber-600 font-semibold uppercase tracking-wider mb-1">Average Premium</p>
                  <p className="text-lg font-bold text-slate-900">${stIns.avg_auto_premium?.toLocaleString() || "N/A"}/year</p>
                </div>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <AlertTriangle size={16} className="text-red-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-red-800">Important: Minimum does NOT mean adequate</p>
                    <p className="text-xs text-red-600 mt-1">State minimums are the bare legal requirement. If you cause an accident that exceeds your limits, you're personally responsible for the difference. Most experts recommend coverage well above minimums.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* RISK FACTORS */}
            {city.risk_factors && city.risk_factors.length > 0 && (
              <section className="bg-white rounded-xl border border-slate-200 p-6" data-testid="risk-factors">
                <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Local Risk Factors in {city.city}</h2>
                <p className="text-sm text-slate-500 mb-4">These factors can affect your auto insurance rates in {city.city}:</p>
                <div className="flex flex-wrap gap-2">
                  {city.risk_factors.map((r, i) => (
                    <span key={i} className="bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-3 py-1 text-xs font-medium capitalize">{r}</span>
                  ))}
                </div>
              </section>
            )}

            {/* COVERAGE GUIDE */}
            <section data-testid="coverage-guide">
              <h2 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Auto Insurance Coverage Types</h2>
              <p className="text-sm text-slate-500 mb-4">Understanding what each type covers helps you make smarter decisions for driving in {city.city}:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[["Liability", "liability", "Required by law — covers others' injuries/damage"], ["Collision", "collision", "Covers your car in a crash"], ["Comprehensive", "comprehensive", "Theft, weather, vandalism"], ["Uninsured Motorist", "uninsured-underinsured", "When the other driver can't pay"], ["Gap Insurance", "gap-insurance", "Covers loan vs. value difference"], ["'Full Coverage' Myth", "full-coverage-myth", "Why it doesn't actually exist"]].map(([name, slug, desc]) => (
                  <Link key={slug} to={`/coverage/${slug}`} onClick={scrollToTop} className="bg-white rounded-lg border border-slate-200 p-4 hover:border-sky-300 hover:shadow-sm transition-all group" data-testid={`cov-link-${slug}`}>
                    <h3 className="text-sm font-bold text-slate-900 group-hover:text-sky-700">{name}</h3>
                    <p className="text-xs text-slate-500 mt-1">{desc}</p>
                  </Link>
                ))}
              </div>
            </section>

            {/* TOP COMPANIES */}
            {city.top_companies && (
              <section className="bg-white rounded-xl border border-slate-200 p-6" data-testid="top-companies">
                <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Top Auto Insurance Companies in {city.state_name || city.state}</h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {city.top_companies.map((c, i) => (
                    <div key={i} className="bg-slate-50 rounded-lg p-3 text-center">
                      <Building size={16} className="text-slate-400 mx-auto mb-1" />
                      <p className="text-xs font-medium text-slate-700">{c}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* MINI QUIZ */}
            <MiniQuiz context="city" cityName={city.city} />

            {/* COMPARE CTA */}
            <div className="bg-sky-700 rounded-xl p-6 text-center" data-testid="city-cta">
              <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Ready to Compare Auto Insurance in {city.city}?</h3>
              <p className="text-sm text-sky-200 mb-4">See how much you could save with a free, no-obligation quote comparison.</p>
              <a href={getAffiliate(settings)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-white text-sky-700 rounded-xl px-6 py-3 text-sm font-semibold hover:bg-sky-50 transition-all" data-testid="city-compare-btn">
                Compare Rates Now <ArrowRight size={14} />
              </a>
            </div>

            {/* MOBILE ADSENSE */}
            <div className="lg:hidden">
              <AdSenseOnly />
            </div>

            {/* QUIZ TEASER */}
            <QuizTeaser />

            {/* NEARBY CITIES */}
            {city.nearby_cities && city.nearby_cities.length > 0 && (
              <section data-testid="nearby-cities">
                <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Nearby Cities</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {city.nearby_cities.map(nc => (
                    <Link key={nc.slug} to={`/quotes/${nc.slug}`} onClick={scrollToTop} className="bg-white rounded-lg border border-slate-200 px-3 py-2.5 text-sm text-slate-700 hover:text-sky-700 hover:border-sky-300 transition-all text-center font-medium">
                      {nc.city}, {nc.state}
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* CAN'T FIND YOUR CITY BANNER */}
            <section className="bg-slate-100 border border-slate-200 rounded-2xl p-6 sm:p-8 text-center" data-testid="cant-find-city-banner">
              <MapPin size={24} className="text-sky-700 mx-auto mb-2" />
              <h3 className="text-lg font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Can't Find Your City?</h3>
              <p className="text-sm text-slate-500 mb-4">Don't worry — our auto insurance guide covers every city in the US. Click below to search for yours.</p>
              <Link to="/find-your-city" onClick={scrollToTop} className="inline-flex items-center gap-2 bg-sky-700 text-white rounded-xl px-6 py-3 text-sm font-medium hover:bg-sky-800 transition-all" data-testid="cant-find-city-btn">
                Auto Insurance Guide for All Cities <ArrowRight size={14} />
              </Link>
            </section>
          </div>

          {/* SIDEBAR */}
          <aside className="w-full lg:w-72 shrink-0 space-y-6">
            <div className="lg:sticky lg:top-20 space-y-6">
              <div className="bg-slate-100 border border-slate-200 rounded-xl p-6 text-center" style={{ minHeight: "600px" }} data-testid="ad-sidebar">
                <p className="text-[10px] text-slate-400 uppercase tracking-widest mb-3">Advertisement</p>
                <p className="text-sm text-slate-300">Google AdSense</p>
                <p className="text-xs text-slate-200 mt-2">300x600</p>
              </div>
              <Link to="/find-your-city" onClick={scrollToTop} className="block bg-white rounded-xl border border-slate-200 p-5 hover:border-sky-300 transition-all">
                <MapPin size={18} className="text-sky-700 mb-2" />
                <h4 className="font-bold text-xs text-slate-900">Can't Find Your City?</h4>
                <p className="text-xs text-slate-500 mt-1">Search any US city for a free auto insurance guide.</p>
              </Link>
            </div>
          </aside>
        </div>
      </div>
      <Footer />
      <ChatBot />
      <ExitIntentPopup />
      <CookieConsent />
    </div>
  );
}
